import React, { useState, useMemo, useRef, useEffect } from "react";
import Layout from "../components/Layout";
import config from "../config";
import "./MiroEstimator.css";

const ROW_TYPE_META = {
  E:   { label: "EPIC", dotClass: "epic",    depth: 0 },
  S:   { label: "STORY", dotClass: "story",   depth: 1 },
  UC:  { label: "UC",    dotClass: "uc",      depth: 2 },
  RED: { label: "RED",   dotClass: "blocker", depth: 2 },
};

const StreamingTree = ({ rows, isStreaming }) => {
  const containerRef = useRef(null);
  const shouldAutoScroll = useRef(true);

  // Track whether user has scrolled up
  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    shouldAutoScroll.current = atBottom;
  };

  // Auto-scroll on new rows
  useEffect(() => {
    const el = containerRef.current;
    if (el && shouldAutoScroll.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [rows.length]);

  // Compute total hours for a story row
  const totalHours = (hours) => {
    if (!hours || typeof hours !== "object") return null;
    const sum = Object.values(hours).reduce((a, b) => a + (b || 0), 0);
    return sum > 0 ? sum : null;
  };

  // Determine depth of the last row (for loading dots indentation)
  const lastDepth = useMemo(() => {
    if (rows.length === 0) return 0;
    const last = rows[rows.length - 1];
    return ROW_TYPE_META[last.row_type]?.depth ?? 0;
  }, [rows]);

  return (
    <div
      className="streaming-tree"
      ref={containerRef}
      onScroll={handleScroll}
    >
      <div className="streaming-tree-header">
        Estimation Tree {rows.length > 0 && `(${rows.length} rows)`}
      </div>

      {rows.length === 0 && !isStreaming && (
        <div className="streaming-tree-empty">
          Submit a Miro board to see the estimation tree
        </div>
      )}

      {rows.length === 0 && isStreaming && (
        <div className="streaming-tree-empty">
          Waiting for AI to start generating rows...
        </div>
      )}

      {rows.map((row, idx) => {
        const meta = ROW_TYPE_META[row.row_type] || { label: "?", dotClass: "story", depth: 1 };
        const hrs = totalHours(row.hours);
        return (
          <div key={idx} className={`tree-row depth-${meta.depth}`}>
            <span className={`tree-dot ${meta.dotClass}`} />
            <span className="tree-type">{meta.label}</span>
            <span className="tree-name" title={row.name}>{row.name}</span>
            {hrs !== null && (
              <span className="tree-hours">{hrs}h</span>
            )}
          </div>
        );
      })}

      {isStreaming && rows.length > 0 && (
        <div className={`streaming-dots depth-${lastDepth}`}>
          <span />
          <span />
          <span />
        </div>
      )}
    </div>
  );
};

const MiroEstimator = () => {
  const [miroUrl, setMiroUrl] = useState("");
  const [sheetTitle, setSheetTitle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [spreadsheetUrl, setSpreadsheetUrl] = useState("");
  const [rowsWritten, setRowsWritten] = useState(null);
  const [streamingRows, setStreamingRows] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSpreadsheetUrl("");
    setRowsWritten(null);
    setStatus("");
    setStreamingRows([]);
    setIsStreaming(false);

    if (!miroUrl.trim()) {
      setError("Please enter a Miro board URL.");
      return;
    }
    if (!sheetTitle.trim()) {
      setError("Please enter a Google Sheet title.");
      return;
    }

    setIsSubmitting(true);

    try {
      // Phase 1: Copy the template
      setStatus("Creating spreadsheet from template...");
      const copyResp = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.miroEstimator.copyTemplate}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sheet_title: sheetTitle.trim() }),
        }
      );
      const copyData = await copyResp.json();
      if (!copyData.success) {
        setError(copyData.error || "Failed to create spreadsheet.");
        return;
      }

      setSpreadsheetUrl(copyData.spreadsheet_url);

      // Phase 2: Stream AI interpretation + sheet population
      setIsStreaming(true);
      const streamResp = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.miroEstimator.populateSheetStream}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            spreadsheet_id: copyData.spreadsheet_id,
            miro_url: miroUrl.trim(),
          }),
        }
      );

      if (!streamResp.ok || !streamResp.body) {
        const data = await streamResp.json().catch(() => ({}));
        setError(data.error || "Failed to start streaming.");
        setIsStreaming(false);
        return;
      }

      const reader = streamResp.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let buffered = "";

      let chunkCount = 0;
      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        const chunk = value ? decoder.decode(value, { stream: true }) : "";
        chunkCount++;
        if (chunk) {
          console.log(`[SSE chunk #${chunkCount}] ${chunk.length} chars:`, chunk.substring(0, 150));
        }
        buffered += chunk;

        let idx;
        while ((idx = buffered.indexOf("\n")) !== -1) {
          const line = buffered.slice(0, idx).trim();
          buffered = buffered.slice(idx + 1);
          if (!line) continue;
          try {
            const msg = JSON.parse(line);
            console.log(`[SSE parsed] type=${msg.type}`, msg.type === "row" ? msg.row?.name : msg.message);
            if (msg.type === "row") {
              setStreamingRows((prev) => [...prev, msg.row]);
            } else if (msg.type === "status") {
              setStatus(msg.message);
            } else if (msg.type === "error") {
              setError(msg.message);
            } else if (msg.type === "done") {
              setRowsWritten(msg.rows_written);
            }
          } catch (parseErr) {
            // ignore malformed lines
          }
        }
      }

      setIsStreaming(false);
      setStatus("");
    } catch (err) {
      setError("Network error. Please try again.");
      setIsStreaming(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="miro-estimator-page">
        <div className="miro-estimator-left">
          <h1>Miro Estimator</h1>
          <p className="miro-estimator-description">
            Enter a Miro board URL and a title for the estimation spreadsheet.
            The Miro board will be parsed and used to populate an estimation
            template in Google Sheets.
          </p>

          <form className="miro-estimator-form" onSubmit={handleSubmit}>
            <div className="miro-estimator-field">
              <label htmlFor="miro-url">Miro Board URL</label>
              <input
                id="miro-url"
                type="text"
                placeholder="https://miro.com/app/board/..."
                value={miroUrl}
                onChange={(e) => setMiroUrl(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className="miro-estimator-field">
              <label htmlFor="sheet-title">Google Sheet Title</label>
              <input
                id="sheet-title"
                type="text"
                placeholder="e.g. Q3 Sprint Estimate"
                value={sheetTitle}
                onChange={(e) => setSheetTitle(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            {error && <div className="miro-estimator-error">{error}</div>}
            {status && <div className="miro-estimator-status">{status}</div>}

            <button
              type="submit"
              className="miro-estimator-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Processing..." : "Create Estimation Sheet"}
            </button>
          </form>

          {spreadsheetUrl && (
            <div className="miro-estimator-result">
              <h3>
                {rowsWritten !== null
                  ? `Spreadsheet populated with ${rowsWritten} rows`
                  : "Spreadsheet Created"}
              </h3>
              <a
                href={spreadsheetUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {spreadsheetUrl}
              </a>
            </div>
          )}
        </div>

        <div className="miro-estimator-right">
          <StreamingTree rows={streamingRows} isStreaming={isStreaming} />
        </div>
      </div>
    </Layout>
  );
};

export default MiroEstimator;
